import { useMemo, useState } from "react";

import { formatBytes } from "@/lib/format";
import { relativeFolderOf, skipReasonLabel, type SkippedItem } from "@/lib/folderUpload";

export type UploadQueueStatus = "queued" | "uploading" | "processing" | "done" | "error" | "canceled";

export interface UploadQueueItem {
  id: string;
  filename: string;
  /** Relativo ao que foi solto/escolhido («Job/Frente/arte.pdf»); igual a `filename` no arquivo solto. */
  relativePath: string;
  size: number;
  progress: number;
  status: UploadQueueStatus;
  error: string | null;
}

interface UploadQueuePanelProps {
  items: UploadQueueItem[];
  skipped: SkippedItem[];
  systemCount: number;
  onClearFinished: () => void;
  onCancelQueued: () => void;
}

/**
 * Uma pasta traz centenas de arquivos: desenhar todos a cada evento de
 * progresso pesa e não informa nada. Acima disto o painel mostra primeiro o
 * que pede atenção (erro, enviando) e resume o resto numa linha.
 */
const MAX_ROWS = 60;
const MAX_SKIPPED_ROWS = 100;

function statusLabel(item: UploadQueueItem): string {
  if (item.status === "queued") return "Na fila";
  if (item.status === "uploading") return "Enviando";
  if (item.status === "processing") return "Finalizando";
  if (item.status === "done") return "Concluído";
  if (item.status === "canceled") return "Cancelado";
  return "Erro";
}

function isActive(item: UploadQueueItem): boolean {
  return item.status === "queued" || item.status === "uploading" || item.status === "processing";
}

export function UploadQueuePanel({
  items,
  skipped,
  systemCount,
  onClearFinished,
  onCancelQueued,
}: UploadQueuePanelProps) {
  const [collapsed, setCollapsed] = useState(false);
  const counts = useMemo(() => {
    let active = 0;
    let queued = 0;
    let done = 0;
    let failed = 0;
    let canceled = 0;
    for (const item of items) {
      if (isActive(item)) active += 1;
      if (item.status === "queued") queued += 1;
      if (item.status === "done") done += 1;
      if (item.status === "error") failed += 1;
      if (item.status === "canceled") canceled += 1;
    }
    return { active, queued, done, failed, canceled };
  }, [items]);

  const rows = useMemo(() => {
    if (items.length <= MAX_ROWS) return items;
    const by = (status: UploadQueueStatus[]) => items.filter((item) => status.includes(item.status));
    return [
      ...by(["error"]),
      ...by(["uploading", "processing"]),
      ...by(["queued"]),
      ...by(["done", "canceled"]),
    ].slice(0, MAX_ROWS);
  }, [items]);

  if (items.length === 0 && skipped.length === 0) return null;

  const summary =
    counts.active > 0
      ? `${counts.done} de ${items.length} concluídos · ${counts.active} em andamento`
      : counts.canceled > 0
        ? `${counts.done} concluídos · ${counts.canceled} cancelados`
        : items.length > 0 && counts.failed === 0
          ? "Tudo concluído"
          : items.length > 0
            ? `${counts.done} concluídos`
            : "Nada enviado";

  return (
    <aside className="fixed bottom-4 right-4 z-50 w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-lg border border-ink-700 bg-ink-900 shadow-2xl">
      <div className="flex items-center justify-between gap-3 border-b border-ink-700 px-3 py-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-ink-100">Uploads</div>
          <div className="text-xs text-ink-400">
            {summary}
            {counts.failed > 0 && <span className="text-red-300">{` · ${counts.failed} com erro`}</span>}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => setCollapsed((value) => !value)}
            className="rounded px-2 py-1 text-xs text-ink-300 hover:bg-ink-800"
            aria-label={collapsed ? "Expandir uploads" : "Minimizar uploads"}
          >
            {collapsed ? "Abrir" : "Min."}
          </button>
          {counts.queued > 0 && (
            <button
              type="button"
              onClick={onCancelQueued}
              className="rounded px-2 py-1 text-xs text-ink-300 hover:bg-ink-800"
              title="Os que já estão subindo terminam; os da fila não começam."
            >
              Cancelar fila
            </button>
          )}
          <button
            type="button"
            onClick={onClearFinished}
            className="rounded px-2 py-1 text-xs text-ink-300 hover:bg-ink-800"
            aria-label="Limpar uploads concluídos"
          >
            Limpar
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="max-h-80 overflow-y-auto">
          {rows.map((item) => {
            const pasta = relativeFolderOf(item.relativePath);
            return (
              <div key={item.id} className="border-b border-ink-800 px-3 py-2 last:border-b-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm text-ink-100" title={item.relativePath}>
                      {item.filename}
                    </div>
                    <div className="truncate text-[11px] text-ink-500" title={item.relativePath}>
                      {pasta ? `${pasta} · ${formatBytes(item.size)}` : formatBytes(item.size)}
                    </div>
                  </div>
                  <div
                    className={`shrink-0 text-xs ${
                      item.status === "error"
                        ? "text-red-300"
                        : item.status === "done"
                          ? "text-emerald-300"
                          : "text-ink-300"
                    }`}
                  >
                    {item.progress}%
                  </div>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-ink-800">
                  <div
                    className={`h-full rounded-full transition-[width] ${
                      item.status === "error" ? "bg-red-500" : "bg-emerald-500"
                    }`}
                    style={{ width: `${item.progress}%` }}
                  />
                </div>
                <div className="mt-1 flex items-center justify-between gap-3 text-[11px]">
                  <span
                    className={
                      item.status === "error"
                        ? "text-red-300"
                        : item.status === "done"
                          ? "text-emerald-300"
                          : "text-ink-400"
                    }
                  >
                    {statusLabel(item)}
                  </span>
                  {item.error && (
                    <span className="min-w-0 truncate text-red-300" title={item.error}>
                      {item.error}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          {items.length > rows.length && (
            <div className="border-b border-ink-800 px-3 py-2 text-[11px] text-ink-500">
              {`+ ${items.length - rows.length} na lista (${counts.queued} na fila, ${counts.done} concluídos)`}
            </div>
          )}
          {skipped.length === 0 && systemCount > 0 && (
            <div className="px-3 py-2 text-[11px] text-ink-500">
              {`${systemCount} arquivo${systemCount === 1 ? "" : "s"} de sistema ignorado${systemCount === 1 ? "" : "s"} (.DS_Store e afins)`}
            </div>
          )}
          {skipped.length > 0 && (
            <details className="px-3 py-2" open={items.length === 0}>
              <summary className="cursor-pointer text-xs text-amber-300">
                {`${skipped.length} ignorado${skipped.length === 1 ? "" : "s"}`}
                {systemCount > 0 && (
                  <span className="text-ink-500">{` · mais ${systemCount} de sistema (.DS_Store e afins)`}</span>
                )}
              </summary>
              <ul className="mt-2 space-y-1">
                {skipped.slice(0, MAX_SKIPPED_ROWS).map((item, index) => (
                  <li
                    key={`${item.relativePath}:${index}`}
                    className="flex items-baseline justify-between gap-3 text-[11px]"
                  >
                    <span className="min-w-0 truncate text-ink-300" title={item.relativePath}>
                      {item.relativePath}
                    </span>
                    <span className="shrink-0 text-ink-500" title={item.detail}>
                      {skipReasonLabel(item.reason)}
                    </span>
                  </li>
                ))}
                {skipped.length > MAX_SKIPPED_ROWS && (
                  <li className="text-[11px] text-ink-500">{`+ ${skipped.length - MAX_SKIPPED_ROWS}`}</li>
                )}
              </ul>
            </details>
          )}
        </div>
      )}
    </aside>
  );
}
